import type { ReactNode } from 'react';
import { Button } from '@/components/base/Button';
import { t } from '@/messages/zh-TW';

/**
 * 第二層版型：錯誤／無權限的整區塊顯示。
 *
 * 原系統對應（**這是一個原系統缺陷，不是我們照抄的設計**）：
 *   `WEB-INF/views/errorContent.jsp` 全檔只有五行 —— 進頁面就跳
 *   `showAlertDialog("Ooops! 程式發生錯誤！")`（`:5`），
 *   然後 `setTimeout(… $("#formContentModal").modal("hide") …, 500)`（`:6`）。
 *   也就是說：**原系統沒有錯誤畫面，只有一顆 alert**，錯誤內容不留在畫面上，
 *   使用者按掉之後沒有任何線索。無權限的情況也沒有專屬頁面（Shiro 直接不 render 選單項）。
 *
 * 因此本元件是「補上原系統沒有的東西」：把錯誤留在畫面上、可讀、可重試。
 * 這一點在模組 Markdown 的「已知原系統缺陷」欄必須記錄，不能寫成 1:1 還原。
 *
 * 【證據等級】§7 第 3 級（原始碼）。【雛形值待實機校準】
 */
export type ErrorFrameVariant = 'error' | 'unauthorized' | 'notFound';

export interface ErrorFrameProps {
  uiId: string;
  variant?: ErrorFrameVariant;
  /** 覆寫標題；預設依 variant 取固定文字。 */
  title?: string;
  /** 補充說明（例如錯誤代碼）。雛形不得放堆疊訊息或個資（§13.5）。 */
  description?: ReactNode;
  /** 有值時畫「重試」鈕。 */
  onRetry?(): void;
  /** 額外操作（例如「回首頁」）。 */
  actions?: ReactNode;
}

function defaultTitle(variant: ErrorFrameVariant): string {
  // notFound 沿用一般錯誤文案：原系統沒有 404 畫面（見上方註解），不自行發明新文字。
  if (variant === 'unauthorized') return t('error.forbiddenTitle');
  return t('error.generic');
}

/**
 * FC-5 Q3 裁決：unauthorized 的說明文字逐字對齊 legacy 錯誤頁的兩段內文
 * （`error.forbiddenDetail` + `error.forbiddenHint`，見 zh-TW.ts 的來源註解）。
 * 只在呼叫端沒有明確傳 `description` 時當預設值使用。
 */
function defaultDescription(variant: ErrorFrameVariant): ReactNode | undefined {
  if (variant !== 'unauthorized') return undefined;
  return (
    <>
      {t('error.forbiddenDetail')}
      <br />
      {t('error.forbiddenHint')}
    </>
  );
}

const TONE = {
  error: 'border-danger text-danger',
  unauthorized: 'border-warning text-warning',
  notFound: 'border-border text-ink-muted',
} as const;

export function ErrorFrame({
  uiId,
  variant = 'error',
  title,
  description,
  onRetry,
  actions,
}: ErrorFrameProps) {
  const resolvedDescription = description ?? defaultDescription(variant);
  return (
    <div
      data-ui-id={uiId}
      role="alert"
      className={`flex flex-col items-center gap-3 border-t-[3px] bg-surface px-4 py-10 text-center ${TONE[variant]}`}
    >
      <p data-ui-id={`${uiId}-title`} className="font-bold text-[18px]">
        {title ?? defaultTitle(variant)}
      </p>
      {resolvedDescription === undefined ? null : (
        <div data-ui-id={`${uiId}-description`} className="text-ink text-sm">
          {resolvedDescription}
        </div>
      )}
      {onRetry === undefined && actions === undefined ? null : (
        <div data-ui-id={`${uiId}-actions`} className="flex flex-wrap justify-center gap-2">
          {onRetry === undefined ? null : (
            <Button
              uiId={`${uiId}-retry`}
              variant="primary"
              size="sm"
              type="button"
              onClick={onRetry}
            >
              {t('action.retry')}
            </Button>
          )}
          {actions}
        </div>
      )}
    </div>
  );
}
